import React, { useLayoutEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Mail, RotateCw } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/seo/SEO";
import { api, getApiErrorStatus } from "@/services/api/client";
import {
  getPublicAuthChallengeFailureMessage,
  getPublicAuthChallengeToken,
  isPublicAuthChallengeFailure,
} from "@/services/api/publicAuthChallenge";
import type {
  ApiResponse,
  LegacyResendVerificationResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
} from "@/types";
import { TIER_DISPLAY } from "@/types/billing";
import {
  normalizePendingRegistrationEmail,
  readPendingRegistrationEmail,
  savePendingRegistrationEmail,
} from "@/utils/pendingRegistrationEmail";
import {
  clearPendingRegistrationSelectedPlan,
  getPendingRegistrationSelectedPlanPath,
  parsePendingRegistrationSelectedPlan,
  readPendingRegistrationSelectedPlan,
  savePendingRegistrationSelectedPlan,
} from "@/utils/pendingRegistrationSelectedPlan";
import {
  parsePendingRegistrationAccountKind,
  readPendingRegistrationAccountKind,
  savePendingRegistrationAccountKind,
  SPONSORSHIP_ACCOUNT_KIND,
} from "@/utils/pendingRegistrationAccountContext";

type ResendState =
  | "idle"
  | "sending"
  | "sent"
  | "failed"
  | "challenge-failed"
  | "unavailable";

const RESEND_ACCEPTED_MESSAGE =
  "If an unverified account exists for that email, a new verification link will be sent.";

const isResendAcceptedResponse = (
  response: ApiResponse<
    ResendVerificationResponse | LegacyResendVerificationResponse
  >,
): boolean => {
  if (response.success !== true || !response.data) {
    return false;
  }

  const resend = response.data;
  if ("sent" in resend) {
    return resend.sent === true && resend.status === "accepted";
  }

  return (
    resend.accepted === true &&
    (resend.status === undefined || resend.status === "accepted")
  );
};

const getResendMessageAccessibility = (
  resendState: ResendState,
): {
  role: "status" | "alert";
  live: "polite" | "assertive";
  className: string;
} => {
  switch (resendState) {
    case "sent":
      return {
        role: "status",
        live: "polite",
        className:
          "rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400",
      };
    case "failed":
    case "challenge-failed":
    case "unavailable":
      return {
        role: "alert",
        live: "assertive",
        className:
          "rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive",
      };
    case "idle":
    case "sending":
    default:
      return {
        role: "status",
        live: "polite",
        className:
          "rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground",
      };
  }
};

export const CheckEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const queryEmail = normalizePendingRegistrationEmail(
    searchParams.get("email") ?? "",
  );
  const initialEmail = queryEmail || readPendingRegistrationEmail();
  const accountKind =
    parsePendingRegistrationAccountKind(searchParams.get("accountKind")) ??
    readPendingRegistrationAccountKind();
  const isSponsorshipRegistration = accountKind === SPONSORSHIP_ACCOUNT_KIND;
  const selectedPlan = useMemo(() => {
    if (isSponsorshipRegistration) {
      return undefined;
    }

    const querySelectedPlan = parsePendingRegistrationSelectedPlan(
      searchParams.get("selectedTier"),
      searchParams.get("billingInterval"),
    );

    return querySelectedPlan ?? readPendingRegistrationSelectedPlan();
  }, [isSponsorshipRegistration, searchParams]);
  const loginPath = useMemo(
    () => getPendingRegistrationSelectedPlanPath("/login", selectedPlan),
    [selectedPlan],
  );
  const registerPath = useMemo(
    () => getPendingRegistrationSelectedPlanPath("/register", selectedPlan),
    [selectedPlan],
  );
  const [email, setEmail] = useState(initialEmail);
  const [resendState, setResendState] = useState<ResendState>("idle");

  useLayoutEffect(() => {
    if (isSponsorshipRegistration) {
      clearPendingRegistrationSelectedPlan();
      savePendingRegistrationAccountKind(SPONSORSHIP_ACCOUNT_KIND);
    }
    if (queryEmail) {
      savePendingRegistrationEmail(queryEmail);
    }
    if (selectedPlan) {
      savePendingRegistrationSelectedPlan(selectedPlan);
    }

    if (!searchParams.has("email") || typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("email");
    window.history.replaceState(
      window.history.state,
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [isSponsorshipRegistration, queryEmail, searchParams, selectedPlan]);

  const handleResend = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return;
    }

    setResendState("sending");

    try {
      const publicAuthChallengeToken = await getPublicAuthChallengeToken(
        "resend-verification",
      );
      const response = await api.post<
        ResendVerificationResponse,
        ResendVerificationRequest
      >(
        "/auth/resend-verification",
        publicAuthChallengeToken
          ? { email: trimmedEmail, publicAuthChallengeToken }
          : { email: trimmedEmail },
      );

      setResendState(isResendAcceptedResponse(response) ? "sent" : "failed");
    } catch (error) {
      if (isPublicAuthChallengeFailure(error)) {
        setResendState("challenge-failed");
        return;
      }

      const status = getApiErrorStatus(error);
      setResendState(
        status === 401 || status === 403 ? "unavailable" : "failed",
      );
    }
  };

  const resendMessage = (() => {
    switch (resendState) {
      case "sent":
        return RESEND_ACCEPTED_MESSAGE;
      case "unavailable":
        return "Verification resend is not available from this page yet. Sign in or contact support if the link has expired.";
      case "challenge-failed":
        return getPublicAuthChallengeFailureMessage();
      case "failed":
        return "We could not process that resend request. Please try again later.";
      case "idle":
      case "sending":
      default:
        return "";
    }
  })();
  const resendMessageAccessibility = getResendMessageAccessibility(resendState);

  return (
    <>
      <SEO
        title="Check Your TerraMatrix Email"
        description="Check your email to verify your TerraMatrix account."
        canonical="/check-email"
        noindex={true}
      />
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <CardTitle>Check Your Email</CardTitle>
          </div>
          <CardDescription>
            Your account is pending email verification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isSponsorshipRegistration && (
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm">
              <span className="font-medium">Account type</span>
              <p className="mt-1 text-muted-foreground">Sub-account</p>
            </div>
          )}
          {selectedPlan && (
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium">Selected plan</span>
                {selectedPlan.tier !== "free" && (
                  <Badge variant="secondary">
                    {selectedPlan.billingInterval === "annual"
                      ? "Annual"
                      : "Monthly"}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-muted-foreground">
                {TIER_DISPLAY[selectedPlan.tier].name} plan
              </p>
            </div>
          )}
          <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 px-3 py-2 rounded-md text-sm">
            {initialEmail
              ? "Check the email address you used for a verification link. After your email is verified, sign in to continue."
              : "Check your inbox for a verification link. After your email is verified, sign in to continue."}
          </div>
          <form onSubmit={handleResend} className="space-y-3">
            <div className="space-y-2">
              <label
                htmlFor="check-email-resend-email"
                className="text-sm font-medium"
              >
                Email
              </label>
              <Input
                id="check-email-resend-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (resendState !== "sending") {
                    setResendState("idle");
                  }
                }}
                placeholder="you@example.com"
                disabled={resendState === "sending"}
                autoComplete="email"
              />
            </div>
            <Button
              type="submit"
              variant="secondary"
              className="w-full"
              disabled={resendState === "sending" || email.trim() === ""}
            >
              {resendState === "sending" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCw className="mr-2 h-4 w-4" />
              )}
              Send verification link
            </Button>
            {resendMessage && (
              <div
                role={resendMessageAccessibility.role}
                aria-live={resendMessageAccessibility.live}
                aria-atomic="true"
                className={resendMessageAccessibility.className}
              >
                {resendMessage}
              </div>
            )}
          </form>
          <Button asChild className="w-full">
            <Link to={loginPath}>Sign in</Link>
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to={registerPath}>
              Use a different email
            </Link>
          </Button>
        </CardContent>
      </Card>
    </>
  );
};
